import type { NavLinkProps } from 'react-router-dom'
import type { ImportStatement } from 'src/types/common/component'

import commonRepo from '@/repository/common.repo'
import WebviewService from '@/service/webview.service'

type PreAction = 'onMouseEnter' | 'onMouseDown'
type LinkPreloadParams<T> = {
  to: NavLinkProps['to']
  importStatement?: ImportStatement<T>
  matchUrl?: string
  preAction?: PreAction
  condition?: (param) => boolean
}

export function onLinkPreload<T>({
  to,
  matchUrl,
  importStatement,
  preAction = 'onMouseEnter',
  condition = () => true,
}: LinkPreloadParams<T>) {
  if (importStatement) {
    const preloadFn = async () => {
      const convertTo = typeof to === 'function' ? to(commonRepo.router.location) : to
      // LocationDescriptor -> string
      const lastTo =
        typeof convertTo === 'string'
          ? convertTo
          : `${convertTo.pathname}${convertTo.search ? '' : convertTo.search}` ?? ''

      if (condition(matchUrl ?? lastTo)) {
        const Component = await importStatement()
        if (Component.default.onMount) {
          Component.default.onMount(lastTo)
        }
      }
    }

    return onPreload({ preloadFn, preAction })
  }
}

type PreloadParams<PreloadFnT extends (...args: any[]) => any> = {
  preAction?: PreAction
  preloadFn: PreloadFnT
}
export const onPreload = <FnT extends (...args: any[]) => any>({
  preAction = 'onMouseEnter',
  preloadFn,
}: PreloadParams<FnT>) => {
  return WebviewService.isMobile
    ? { onTouchStart: preloadFn }
    : { [preAction as PreAction]: preloadFn }
}
